import CloseIcon from '@mui/icons-material/Close';
import { Box, Button } from '@mui/material';
import { useEffect, useState } from 'react';
import { useAtomValue, useSetAtom } from 'jotai';
import { Business_Profile_Enum } from '@kheops/graphql';
import { useTranslation } from 'react-i18next';
import { ContractPreview } from '../hooks/useContracts';
import { currentContextAtom } from '../state';
import { commonSnackbarPropsAtom } from '../common/state/state';
import { floatingCardEntranceSx } from '../common/components/floatingCardEntrance';
import TrustedSupplierCard from './TrustedSupplierCard';
import useGetValidatedOrdersOnFirstTryCount from './useGetValidatedOrdersOnFirstTryCount';
import { canEditContractConditionsAtom } from './state';

interface TrustedSupplierFloatingCardProps {
  contract: ContractPreview;
  onSnackbarActionClick: () => void;
  delayInMS?: number;
}

const TRUSTED_SUPPLIER_CARD_ORDERS_COUNT_THRESHOLD = 5;

export default function TrustedSupplierFloatingCard({ contract, onSnackbarActionClick, delayInMS = 1 }: TrustedSupplierFloatingCardProps): React.JSX.Element {
  const { t } = useTranslation(['contracts', 'common']);
  const context = useAtomValue(currentContextAtom);
  const canEditContractConditions = useAtomValue(canEditContractConditionsAtom);
  const [shouldDisplayTrustedSupplierCard, setShouldDisplayTrustedSupplierCard] = useState(false);
  const orderSuggestionsValidatedDirectlyCount = useGetValidatedOrdersOnFirstTryCount({
    buyerCompanyId: contract?.buyingCompanyId,
    supplierCompanyId: contract?.supplyingCompanyId,
  });
  const setCommonSnackbarProps = useSetAtom(commonSnackbarPropsAtom);

  useEffect(() => {
    let timer: NodeJS.Timeout;

    if (contract && !contract.is_trusted_supplier && context.realm === Business_Profile_Enum.Buyer && canEditContractConditions) {
      const validationCount = localStorage.getItem(`${contract.reference_id!}-validation-count`);
      const formattedValidationCount = validationCount ? Number(validationCount) : 0;

      if (orderSuggestionsValidatedDirectlyCount - formattedValidationCount >= TRUSTED_SUPPLIER_CARD_ORDERS_COUNT_THRESHOLD) {
        timer = setTimeout(() => {
          setShouldDisplayTrustedSupplierCard(true);
        }, delayInMS);
      }
    }

    return () => {
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [orderSuggestionsValidatedDirectlyCount, canEditContractConditions]);

  const setUpNextTimeDisplaycondition = (): void => {
    localStorage.setItem(`${contract.reference_id!}-validation-count`, orderSuggestionsValidatedDirectlyCount.toString());
  };

  const handleCloseTrustedSupplierCard = (): void => {
    setShouldDisplayTrustedSupplierCard(false);
    setCommonSnackbarProps({
      label: t('contracts:trusted_supplier.find_this_feature_in'),
      snackbarProps: {
        open: true,
      },
      alertProps: {
        severity: 'info',
        action: (
          <Button onClick={onSnackbarActionClick}>
            {t('common:see')}
          </Button>
        ),
      },
    });
    setUpNextTimeDisplaycondition();
  };

  const handleTrustedSupplierCardAction = (activate: boolean): void => {
    if (activate) {
      setCommonSnackbarProps({
        label: t('contracts:trusted_supplier.find_this_feature_in'),
        snackbarProps: {
          open: true,
        },
        alertProps: {
          action: (
            <Button color="success" onClick={onSnackbarActionClick}>
              {t('contracts:see_the_contract')}
            </Button>
          ),
        },
      });
    } else {
      setCommonSnackbarProps({
        label: t('contracts:trusted_supplier.find_this_feature_in'),
        snackbarProps: {
          open: true,
        },
        alertProps: {
          severity: 'info',
          action: (
            <Button onClick={onSnackbarActionClick}>
              {t('common:see')}
            </Button>
          ),
        },
      });
    }
    setUpNextTimeDisplaycondition();
  };

  return (
    // eslint-disable-next-line react/jsx-no-useless-fragment
    <>
      {shouldDisplayTrustedSupplierCard && (
        <Box
          sx={{
            ...floatingCardEntranceSx,
            display: 'flex',
            flexDirection: 'column',
            position: 'fixed',
            bottom: {
              xs: 8,
              sm: 12,
              lg: 16,
            },
            right: {
              xs: 8,
              sm: 12,
              lg: 16,
            },
            zIndex: 1002,
          }}
        >
          <Button
            sx={{ width: 'fit-content', marginLeft: 'auto', mb: 1.5 }}
            variant="tonal"
            onClick={handleCloseTrustedSupplierCard}
          >
            <CloseIcon />
          </Button>
          <TrustedSupplierCard
            onAction={handleTrustedSupplierCardAction}
            onClose={() => setShouldDisplayTrustedSupplierCard(false)}
            contract={contract!}
            imageSx={{
              height: {
                xs: 97,
                md: 'auto',
              },
            }}
            sx={{
              ml: 1,
              pl: {
                xs: 1,
                md: 2,
              },
              width: {
                sm: 308,
                md: 616,
                lg: 640,
              },
              maxWidth: {
                xs: 308,
                sm: 'inherit',
              },
              height: {
                xs: 'auto',
                md: 150,
                lg: 166,
              },
            }}
          />
        </Box>
      )}
    </>
  );
}
